import React from "react";
import "./MateCard.css";
import { Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faCode } from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import ReactJson from "react-json-view";
import { deviceTypeBadgeColor, tagColor } from "../../../../../../lib/ui/colorUtils";
import { truncateFirmwareVersion, formatTargetName } from "../../../../../../lib/ui/badgeUtils";

function MateCard({ mateData }) {
    if (!mateData) {
        return null;
    }

    const sysVersion = mateData.sys?.version || {};
    const sysLocale = mateData.sys?.locale || {};
    const channels = mateData.channels || {};
    const sidekicks = mateData.sidekicks || {};

    // Get mate information from sidekicks object (keyed by bond_id)
    const mateId = mateData.bond_id || mateData.id;
    const mateInfo = sidekicks[mateId] || {};

    // Filter out metadata fields from channels
    const channelEntries = Object.entries(channels).filter(([key]) => !key.startsWith('_'));

    return (
        <Card>
            <Card.Body className="pe-3 ps-3 pb-3">
                {/* Basic Mate Information */}
                <div className="p-3">
                    <FloatingLabel label="Mate ID" className="mb-2">
                        <Form.Control readOnly={true} value={mateData.bond_id || mateData.id} />
                    </FloatingLabel>

                    <div className="d-flex gap-2 mb-2">
                        {sysVersion.make && (
                            <FloatingLabel label="Make" className="flex-fill">
                                <Form.Control readOnly={true} value={sysVersion.make} />
                            </FloatingLabel>
                        )}
                        {sysVersion.model && (
                            <FloatingLabel label="Model" className="flex-fill">
                                <Form.Control readOnly={true} value={sysVersion.model} />
                            </FloatingLabel>
                        )}
                    </div>

                    <div className="d-flex gap-2 mb-2">
                        {sysVersion.fw_ver && (
                            <FloatingLabel label="Firmware Version" className="flex-fill">
                                <Form.Control readOnly={true} value={truncateFirmwareVersion(sysVersion.fw_ver)} title={sysVersion.fw_ver} />
                            </FloatingLabel>
                        )}
                        {sysVersion.target && (
                            <FloatingLabel label="Target" className="flex-fill">
                                <Form.Control readOnly={true} value={formatTargetName(sysVersion.target)} />
                            </FloatingLabel>
                        )}
                    </div>

                    {sysLocale.compliance_region && (
                        <FloatingLabel label="Compliance Region" className="mb-2">
                            <Form.Control readOnly={true} value={sysLocale.compliance_region.toUpperCase()} />
                        </FloatingLabel>
                    )}

                    {/* Mate Device Information */}
                    {mateInfo && Object.keys(mateInfo).length > 0 && (
                        <>
                            <div className="d-flex gap-2 mb-2">
                                {mateInfo.name && (
                                    <FloatingLabel label="Name" className="flex-fill">
                                        <Form.Control readOnly={true} value={mateInfo.name} />
                                    </FloatingLabel>
                                )}
                                {mateInfo.location && (
                                    <FloatingLabel label="Location" className="flex-fill">
                                        <Form.Control readOnly={true} value={mateInfo.location} />
                                    </FloatingLabel>
                                )}
                                {mateInfo.type && (
                                    <FloatingLabel label="Type" className="flex-fill">
                                        <Form.Control readOnly={true} value={mateInfo.type} />
                                    </FloatingLabel>
                                )}
                            </div>

                            <div className="d-flex gap-2 mb-2">
                                {mateInfo.model && (
                                    <FloatingLabel label="Model" className="flex-fill">
                                        <Form.Control readOnly={true} value={mateInfo.model} />
                                    </FloatingLabel>
                                )}
                                {mateInfo.chans !== undefined && (
                                    <FloatingLabel label="Channels" className="flex-fill">
                                        <Form.Control readOnly={true} value={mateInfo.chans} />
                                    </FloatingLabel>
                                )}
                                {mateInfo.keys !== undefined && (
                                    <FloatingLabel label="Keys" className="flex-fill">
                                        <Form.Control readOnly={true} value={mateInfo.keys} />
                                    </FloatingLabel>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Channels Section */}
                {channelEntries.length > 0 && (
                    <Accordion defaultActiveKey="" className="mb-2">
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <div className="justify-content-between w-100">
                                    <div className="float-start">
                                        <FontAwesomeIcon icon={faCircle} className="me-2" />
                                        Channels
                                    </div>
                                    <Badge bg="info" className="float-end">
                                        {channelEntries.length}
                                    </Badge>
                                </div>
                            </Accordion.Header>
                            <Accordion.Body className="p-3">
                                <Accordion>
                                    {channelEntries.map(([channelNum, channel]) => (
                                        <Accordion.Item eventKey={`channel-${channelNum}`} key={channelNum}>
                                            <Accordion.Header>
                                                <div className="d-flex align-items-center w-100" style={{gap: '0.5rem'}}>
                                                    <Badge bg="primary" className="flex-shrink-0">
                                                        Ch {channelNum}
                                                    </Badge>
                                                    {channel.type && (
                                                        <Badge className="flex-shrink-0" style={deviceTypeBadgeColor(channel.type)}>
                                                            {channel.type}
                                                        </Badge>
                                                    )}
                                                    {channel.template && (
                                                        <Badge bg="info" className="flex-shrink-0" style={tagColor("template", channel.template)}>
                                                            {channel.template}
                                                        </Badge>
                                                    )}
                                                    {!channel.template && (channel.label || channel.name) && (
                                                        <Badge bg="warning" className="flex-shrink-0">RAW</Badge>
                                                    )}
                                                    {!channel.template && !channel.label && !channel.name ? (
                                                        <Badge bg="info" className="flex-shrink-0">(empty)</Badge>
                                                    ) : (
                                                        <span className="flex-grow-1 text-truncate" style={{minWidth: 0}}>
                                                            {channel.label || channel.name || ''}
                                                        </span>
                                                    )}
                                                    <div className="d-flex flex-shrink-0" style={{gap: '0.25rem'}}>
                                                        {channel.properties?.pairing_assets_key && (
                                                            <Badge bg="dark">
                                                                {channel.properties.pairing_assets_key}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            </Accordion.Header>
                                            <Accordion.Body className="p-3">
                                                {channel.label && (
                                                    <FloatingLabel label="Label" className="mb-2">
                                                        <Form.Control readOnly={true} value={channel.label} />
                                                    </FloatingLabel>
                                                )}
                                                {channel.name && (
                                                    <FloatingLabel label="Name" className="mb-2">
                                                        <Form.Control readOnly={true} value={channel.name} />
                                                    </FloatingLabel>
                                                )}
                                                {channel.type && (
                                                    <FloatingLabel label="Type" className="mb-2">
                                                        <Form.Control readOnly={true} value={channel.type} />
                                                    </FloatingLabel>
                                                )}
                                                {channel.template && (
                                                    <FloatingLabel label="Template" className="mb-2">
                                                        <Form.Control readOnly={true} value={channel.template} />
                                                    </FloatingLabel>
                                                )}

                                                {/* Properties */}
                                                {channel.properties && Object.keys(channel.properties).length > 0 && (
                                                    <div className="mt-3">
                                                        <h6>Properties</h6>
                                                        {Object.entries(channel.properties).filter(([key]) => !key.startsWith('_')).map(([key, value], idx) => (
                                                            <FloatingLabel key={idx} label={key} className="mb-2">
                                                                <Form.Control readOnly={true} value={String(value)} />
                                                            </FloatingLabel>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Raw JSON */}
                                                <div className="mt-3">
                                                    <Accordion>
                                                        <Accordion.Item eventKey="json">
                                                            <Accordion.Header>
                                                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                                                Raw JSON
                                                            </Accordion.Header>
                                                            <Accordion.Body>
                                                                <ReactJson
                                                                    src={channel}
                                                                    theme="ocean"
                                                                    collapsed={1}
                                                                    displayDataTypes={false}
                                                                    displayObjectSize={false}
                                                                    enableClipboard={true}
                                                                    name={false}
                                                                />
                                                            </Accordion.Body>
                                                        </Accordion.Item>
                                                    </Accordion>
                                                </div>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                )}

                {/* Raw JSON Viewer */}
                <div className="mt-3">
                    <Accordion>
                        <Accordion.Item eventKey="json">
                            <Accordion.Header>
                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                Raw JSON
                            </Accordion.Header>
                            <Accordion.Body>
                                <ReactJson
                                    src={mateData}
                                    theme="ocean"
                                    collapsed={1}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    enableClipboard={true}
                                    name={false}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>
            </Card.Body>
        </Card>
    );
}

export default MateCard;
